/**
 * Feature-flag registry (code-owned definitions).
 *
 * A flag is DEFINED in code and only its *overrides* are stored
 * (`feature_flag_overrides` table). Definitions carry a typed default, so a
 * flag always resolves to a value and can never be "set" to something the
 * running code doesn't understand — a stored override for an unknown key is
 * simply ignored at resolution.
 *
 * Same discipline as `permissions.ts` (`PERMISSIONS`) and `roles.ts`
 * (`ROLE_PERMISSIONS`): a closed `satisfies` tuple, diff-reviewable, giving a
 * literal `FlagKey` union for free.
 *
 * Resolution (management-side, at token issue): workspace override → org
 * default → `FlagDefinition.default`. The app reads the resolved values from
 * the principal/JWT, never from the table (split-ability).
 *
 * Renaming a `key` is BREAKING (override rows reference it by string). Treat
 * keys as PERMANENT, like permission slugs.
 *
 * See `aidlc-docs/construction/onboarding-connectors/proposals/b2-feature-flags-technical-design.md`.
 */
/**
 * Closed registry. Adding a flag = add a row here. B2 ships a single boolean
 * flag; entitlement flags arrive with the pricing rules.
 */
export const FLAG_DEFINITIONS = [
    {
        key: "surface.onboarding_only",
        kind: "boolean",
        default: false,
        transport: "claim",
        internalOnly: false,
        description: "When true, the workspace is confined to the /onboard wizard surface; " +
            "the full dashboard is hidden. Subsumes the onboardingState-derived surface gate.",
    },
    {
        key: "surface.workflows",
        kind: "boolean",
        default: false,
        transport: "claim",
        internalOnly: true,
        description: "Shows the Workflows surface (nav section, routes, and the workflow " +
            "permission sections in the role editors). Default OFF so environments " +
            "without workflow infra (Temporal + workers) never surface it; flip the " +
            "override on per workspace once the infra is live. Enforced end-to-end: " +
            "the dashboard hides the surface AND the api/workflows routes 403 (the " +
            "API's RequireFlagGuard resolves the flag from the DB per request, so " +
            "flipping it off applies immediately).",
    },
];
const BY_KEY = new Map(FLAG_DEFINITIONS.map((d) => [d.key, d]));
export function flagDefinition(key) {
    return BY_KEY.get(key);
}
/** Keys whose resolved value is carried in the JWT (`transport: "claim"`). */
export const CLAIM_FLAG_KEYS = FLAG_DEFINITIONS.filter((d) => d.transport === "claim").map((d) => d.key);
/**
 * The complete default map — every registered flag at its compiled-in default.
 * The resolver folds override rows over this; the app falls back to it for
 * tokens minted before the `flags` claim existed.
 */
export function defaultFlags() {
    const out = {};
    for (const def of FLAG_DEFINITIONS)
        out[def.key] = def.default;
    return out;
}
//# sourceMappingURL=feature-flags.js.map